import { IUseWalletStore } from '@/src/store/global/types';
import { EMERGENCY_ADMIN_TOKEN_CS } from '@/src/utils/constants';
import { applyParamsToScript, Data, MintingPolicy, mintingPolicyToId, ScriptHash, sleep, Validator, validatorToAddress, validatorToScriptHash } from '@lucid-evolution/lucid';
import {
    explainErrorTx,
    formatHash,
    FundFactory,
    FundFactoryCreateParams,
    getScriptFromJson,
    isEmulator,
    LUCID_NETWORK_MAINNET_NAME,
    LUCID_NETWORK_PREVIEW_NAME,
    optionsGetMinimal,
    ProtocolAddScriptsTxParams,
    ProtocolCreateParams,
    ProtocolDeleteTxParams,
    ProtocolDeployTxParams,
    ProtocolUpdateParams,
    ProtocolUpdateTxParams,
    pushSucessNotification,
    pushWarningNotification,
    TX_PROPAGATION_DELAY_MS,
} from '../../Commons';
import { DelegationEntity, SwapOfferEntity } from '../../Entities';
import { ProtocolEntity } from '../../Entities/Protocol.Entity';
import { ScriptEntity } from '../../Entities/Script.Entity';
import { JobManagerFrontEnd, LucidToolsFrontEnd } from '../../lib';
import { ScriptApi } from '../Script/Script.FrontEnd.Api.Calls';
import { ScriptTxApiCalls } from '../Script/Script.FrontEnd.Tx.Api.Calls';
import { AddressToFollowApi } from '../index.exports';
import { SmartDBBtnHandlers } from '../index.exports.BtnHandlers.TxApiCalls';
import { ProtocolApi } from './Protocol.FrontEnd.Api.Calls';
import { ProtocolTxApiCalls } from './Protocol.FrontEnd.Tx.Api.Calls';

export class ProtocolBtnHandlers extends SmartDBBtnHandlers {
    protected static _Entity = ProtocolEntity;
    protected static _TxApiCalls = ProtocolTxApiCalls;

    // #region generic methods

    // #endregion generic methods

    // #region front end Btn handlers

    public static async handleBtnProtocolCreate(walletStore: IUseWalletStore, params: ProtocolCreateParams): Promise<string | undefined> {
        try {
            //--------------------------------------
            let protocolFactory;
            try {
                const configJsonObject = JSON.parse(params.configJson);
                //--------------------------------------
                protocolFactory = {
                    ...configJsonObject,
                    fpProtocolPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpProtocolPolicyID_Pre_CborHex),
                    fpProtocolValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpProtocolValidator_Pre_CborHex),
                    fpScriptPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpScriptPolicyID_Pre_CborHex),
                    fpScriptValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpScriptValidator_Pre_CborHex),
                    fpSwapOfferPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpSwapOfferPolicyID_Pre_CborHex),
                    fpSwapOfferValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpSwapOfferValidator_Pre_CborHex),
                    fpBuyOrderPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpBuyOrderPolicyID_Pre_CborHex),
                    fpBuyOrderValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpBuyOrderValidator_Pre_CborHex),
                    fpDelegationPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpDelegationPolicyID_Pre_CborHex),
                    fpDelegationValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpDelegationValidator_Pre_CborHex),
                    fpInvestUnitValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpInvestUnitValidator_Pre_CborHex),
                    fpFundPolicy_Pre_CborHex: getScriptFromJson(configJsonObject.fpFundPolicy_Pre_CborHex),
                    fpFundValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpFundValidator_Pre_CborHex),
                    fpFundHoldingPolicyID_Pre_CborHex: getScriptFromJson(configJsonObject.fpFundHoldingPolicyID_Pre_CborHex),
                    fpFundHoldingValidator_Pre_CborHex: getScriptFromJson(configJsonObject.fpFundHoldingValidator_Pre_CborHex),
                };
            } catch (error) {
                throw 'Invalid config json file ' + error;
            }
            //--------------------------------------
            const lucid = await walletStore.getLucid();
            if (lucid === undefined) {
                throw 'Please connect your wallet';
            }
            //--------------------------------------
            if (walletStore.isWalletDataLoaded !== true) {
                throw 'Wallet Data is not ready';
            }
            if (walletStore.getUTxOsAtWallet().length === 0) {
                throw 'You need at least one utxo to be used to mint Protocol ID';
            }
            //--------------------------------------
            const walletUTxOs = walletStore.getUTxOsAtWallet();
            if (walletUTxOs.length === 0) {
                throw 'You need at least one utxo to be used to mint Protocol ID';
            }
            const uTxO = walletUTxOs[0];
            console.log(`uTxO for creating Protocol ID: ${uTxO}`);
            //--------------------------------------
            const protocol_TxHash = uTxO.txHash;
            const protocol_TxOutputIndex = uTxO.outputIndex;
            //--------------------------------------
            // Protocol Policy
            const ParamsSchemaProtocolPolicyID = Data.Tuple([Data.Bytes(), Data.Integer()]);
            type ParamsProtocolPolicy = Data.Static<typeof ParamsSchemaProtocolPolicyID>;
            //--------------------------------------
            const pdpProtocolPolicyID_Params = {
                protocol_TxHash,
                protocol_TxOutputIndex: BigInt(protocol_TxOutputIndex),
            };
            //--------------------------------------
            const pdpProtocolPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsProtocolPolicy>(
                    protocolFactory.fpProtocolPolicyID_Pre_CborHex.script,
                    [pdpProtocolPolicyID_Params.protocol_TxHash, pdpProtocolPolicyID_Params.protocol_TxOutputIndex],
                    ParamsSchemaProtocolPolicyID as unknown as ParamsProtocolPolicy
                ),
            };
            //--------------------------------------
            const pdpProtocolPolicyID_CS = mintingPolicyToId(pdpProtocolPolicyID_Script);
            console.log(`pdpProtocolPolicyID_CS ${pdpProtocolPolicyID_CS}`);
            //--------------------------------------
            // Protocol Validator
            const ParamsSchemaProtocolValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsProtocolValidator = Data.Static<typeof ParamsSchemaProtocolValidator>;
            //--------------------------------------
            const pdpProtocolValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS,
            };
            //--------------------------------------
            const pdpProtocolValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsProtocolValidator>(
                    protocolFactory.fpProtocolValidator_Pre_CborHex.script,
                    [pdpProtocolValidator_Params.protocolPolicyID_CS, pdpProtocolValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsSchemaProtocolValidator as unknown as ParamsProtocolValidator
                ),
            };
            //--------------------------------------
            const pdpProtocolValidator_Hash = validatorToScriptHash(pdpProtocolValidator_Script);
            console.log(`pdpProtocolValidator_Hash ${pdpProtocolValidator_Hash}`);
            //--------------------------------------
            const pdpProtocolValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpProtocolValidator_Script);
            console.log(`pdpProtocolValidator_AddressTestnet ${pdpProtocolValidator_AddressTestnet}`);
            const pdpProtocolValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpProtocolValidator_Script);
            console.log(`pdpProtocolValidator_AddressMainnet ${pdpProtocolValidator_AddressMainnet}`);
            //--------------------------------------
            // Script Policy
            const ParamsSchemaScriptPolicyID = Data.Tuple([Data.Bytes()]);
            type ParamsScriptPolicy = Data.Static<typeof ParamsSchemaScriptPolicyID>;
            //--------------------------------------
            const pdpScriptPolicyID_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
            };
            //--------------------------------------
            const pdpScriptPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsScriptPolicy>(
                    protocolFactory.fpScriptPolicyID_Pre_CborHex.script,
                    [pdpScriptPolicyID_Params.protocolPolicyID_CS],
                    ParamsSchemaScriptPolicyID as unknown as ParamsScriptPolicy
                ),
            };
            //--------------------------------------
            const pdpScriptPolicyID_CS = mintingPolicyToId(pdpScriptPolicyID_Script);
            console.log(`pdpScriptPolicyID_CS ${pdpScriptPolicyID_CS}`);
            //--------------------------------------
            // Script Validator
            const ParamsSchemaScriptValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsScriptValidator = Data.Static<typeof ParamsSchemaScriptValidator>;
            //--------------------------------------
            const pdpScriptValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                scriptPolicyID_CS: pdpScriptPolicyID_CS,
            };
            //--------------------------------------
            const pdpScriptValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsScriptValidator>(
                    protocolFactory.fpScriptValidator_Pre_CborHex.script,
                    [pdpScriptValidator_Params.protocolPolicyID_CS, pdpScriptValidator_Params.scriptPolicyID_CS],
                    ParamsSchemaScriptValidator as unknown as ParamsScriptValidator
                ),
            };
            //--------------------------------------
            const pdpScriptValidator_Hash = validatorToScriptHash(pdpScriptValidator_Script);
            console.log(`pdpScriptValidator_Hash ${pdpScriptValidator_Hash}`);
            //--------------------------------------
            const pdpScriptValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpScriptValidator_Script);
            console.log(`pdpScriptValidator_AddressTestnet ${pdpScriptValidator_AddressTestnet}`);
            const pdpScriptValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpScriptValidator_Script);
            console.log(`pdpScriptValidator_AddressMainnet ${pdpScriptValidator_AddressMainnet}`);
            //--------------------------------------
            // SwapOffer Validator
            const ParamsSchemaSwapOfferValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsSwapOfferValidator = Data.Static<typeof ParamsSchemaSwapOfferValidator>;
            //--------------------------------------
            const pdpSwapOfferValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS,
            };
            //--------------------------------------
            const pdpSwapOfferValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsSwapOfferValidator>(
                    protocolFactory.fpSwapOfferValidator_Pre_CborHex.script,
                    [pdpSwapOfferValidator_Params.protocolPolicyID_CS, pdpSwapOfferValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsSchemaSwapOfferValidator as unknown as ParamsSwapOfferValidator
                ),
            };
            //--------------------------------------
            const pdpSwapOfferValidator_Hash = validatorToScriptHash(pdpSwapOfferValidator_Script);
            console.log(`pdpSwapOfferValidator_Hash ${pdpSwapOfferValidator_Hash}`);
            //--------------------------------------
            const pdpSwapOfferValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpSwapOfferValidator_Script);
            console.log(`pdpSwapOfferValidator_AddressTestnet ${pdpSwapOfferValidator_AddressTestnet}`);
            const pdpSwapOfferValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpSwapOfferValidator_Script);
            console.log(`pdpSwapOfferValidator_AddressMainnet ${pdpSwapOfferValidator_AddressMainnet}`);
            //--------------------------------------
            // const mayx_stake_PKH = addressToStakePubKeyHash(MAYZ_WALLET_CONTROL_STAKE_DELEGATION);
            // //--------------------------------------
            // const pdpSwapOfferValidator_AddressTestnet_PKH = addressToPubKeyHash(pdpSwapOfferValidator_AddressTestnet);
            // if (mayx_stake_PKH === undefined || pdpSwapOfferValidator_AddressTestnet_PKH === undefined) {
            //     throw `Can't find validator_address or mayx_Stake`;
            // }
            // const pdpSwapOfferValidator_AddressTestnetWithStake = pubKeyHashToAddress(LUCID_NETWORK_TESTNET_INT, pdpSwapOfferValidator_AddressTestnet_PKH, mayx_stake_PKH);
            // console.log(`pdpSwapOfferValidator_AddressTestnetWithStake ${pdpSwapOfferValidator_AddressTestnetWithStake}`);
            // //--------------------------------------
            // const pdpSwapOfferValidator_AddressMainnet_PKH = addressToPubKeyHash(pdpSwapOfferValidator_AddressMainnet);
            // if (mayx_stake_PKH === undefined || pdpSwapOfferValidator_AddressMainnet_PKH === undefined) {
            //     throw `Can't find validator_address or mayx_Stake`;
            // }
            // const pdpSwapOfferValidator_AddressMainnetWithStake = pubKeyHashToAddress(LUCID_NETWORK_MAINNET_INT, pdpSwapOfferValidator_AddressMainnet_PKH, mayx_stake_PKH);
            // console.log(`pdpSwapOfferValidator_AddressMainnetWithStake ${pdpSwapOfferValidator_AddressMainnetWithStake}`);
            //--------------------------------------
            // SwapOffer PolicyID
            const ParamsSchemaSwapOfferPolicy = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsSwapOfferPolicy = Data.Static<typeof ParamsSchemaSwapOfferPolicy>;
            //--------------------------------------
            const pdpSwapOfferPolicyID_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                swapOfferValidator_Hash: pdpSwapOfferValidator_Hash,
            };
            //--------------------------------------
            const pdpSwapOfferPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsSwapOfferPolicy>(
                    protocolFactory.fpSwapOfferPolicyID_Pre_CborHex.script,
                    [pdpSwapOfferPolicyID_Params.protocolPolicyID_CS, pdpSwapOfferPolicyID_Params.swapOfferValidator_Hash],
                    ParamsSchemaSwapOfferPolicy as unknown as ParamsSwapOfferPolicy
                ),
            };
            //--------------------------------------
            const pdpSwapOfferPolicyID_CS = mintingPolicyToId(pdpSwapOfferPolicyID_Script);
            console.log(`pdpSwapOfferPolicyID_CS ${pdpSwapOfferPolicyID_CS}`);
            //--------------------------------------
            // BuyOrder Validator first (needed for PolicyID params)
            const ParamsSchemaBuyOrderValidator = Data.Tuple([Data.Bytes()]);
            type ParamsBuyOrderValidator = Data.Static<typeof ParamsSchemaBuyOrderValidator>;
            //--------------------------------------
            const pdpBuyOrderValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
            };
            //--------------------------------------
            const pdpBuyOrderValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsBuyOrderValidator>(
                    protocolFactory.fpBuyOrderValidator_Pre_CborHex.script,
                    [pdpBuyOrderValidator_Params.protocolPolicyID_CS],
                    ParamsSchemaBuyOrderValidator as unknown as ParamsBuyOrderValidator
                ),
            };
            //--------------------------------------
            const pdpBuyOrderValidator_Hash = validatorToScriptHash(pdpBuyOrderValidator_Script);
            console.log(`pdpBuyOrderValidator_Hash ${pdpBuyOrderValidator_Hash}`);
            //--------------------------------------
            const pdpBuyOrderValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpBuyOrderValidator_Script);
            console.log(`pdpBuyOrderValidator_AddressTestnet ${pdpBuyOrderValidator_AddressTestnet}`);
            const pdpBuyOrderValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpBuyOrderValidator_Script);
            console.log(`pdpBuyOrderValidator_AddressMainnet ${pdpBuyOrderValidator_AddressMainnet}`);
            //--------------------------------------
            // BuyOrder PolicyID
            const ParamsSchemaBuyOrderPolicyID = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsBuyOrderPolicy = Data.Static<typeof ParamsSchemaBuyOrderPolicyID>;
            //--------------------------------------
            const pdpBuyOrderPolicyID_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                buyOrderValidator_Hash: pdpBuyOrderValidator_Hash,
            };
            //--------------------------------------
            const pdpBuyOrderPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsBuyOrderPolicy>(
                    protocolFactory.fpBuyOrderPolicyID_Pre_CborHex.script,
                    [pdpBuyOrderPolicyID_Params.protocolPolicyID_CS, pdpBuyOrderPolicyID_Params.buyOrderValidator_Hash],
                    ParamsSchemaBuyOrderPolicyID as unknown as ParamsBuyOrderPolicy
                ),
            };
            //--------------------------------------
            const pdpBuyOrderPolicyID_CS = mintingPolicyToId(pdpBuyOrderPolicyID_Script);
            console.log(`pdpBuyOrderPolicyID_CS ${pdpBuyOrderPolicyID_CS}`);
            //--------------------------------------
            const ParamsSchemaDelegationValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsDelegationValidator = Data.Static<typeof ParamsSchemaDelegationValidator>;
            const ParamsDelegationValidator = ParamsSchemaDelegationValidator as unknown as ParamsDelegationValidator;
            //--------------------------------------
            const pdpDelegationValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS,
            };
            //--------------------------------------
            const pdpDelegationValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsDelegationValidator>(
                    protocolFactory.fpDelegationValidator_Pre_CborHex.script,
                    [pdpDelegationValidator_Params.protocolPolicyID_CS, pdpDelegationValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsSchemaDelegationValidator as unknown as ParamsDelegationValidator
                ),
            };
            //--------------------------------------
            const pdpDelegationValidator_Hash = validatorToScriptHash(pdpDelegationValidator_Script);
            console.log(`pdpDelegationValidator_Hash ${pdpDelegationValidator_Hash}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_PREVIEW_NAME;
            const pdpDelegationValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpDelegationValidator_Script);
            console.log(`pdpDelegationValidator_AddressTestnet ${pdpDelegationValidator_AddressTestnet}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_MAINNET_NAME;
            const pdpDelegationValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpDelegationValidator_Script);
            console.log(`pdpDelegationValidator_AddressMainnet ${pdpDelegationValidator_AddressMainnet}`);
            //--------------------------------------
            const ParamsSchemaDelegationPolicy = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsDelegationPolicy = Data.Static<typeof ParamsSchemaDelegationPolicy>;
            const ParamsDelegationPolicy = ParamsSchemaDelegationPolicy as unknown as ParamsDelegationPolicy;
            //--------------------------------------
            const pdpDelegationPolicyID_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                delegationValidator_Hash: pdpDelegationValidator_Hash,
            };
            //--------------------------------------
            const pdpDelegationPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsDelegationPolicy>(
                    protocolFactory.fpDelegationPolicyID_Pre_CborHex.script,
                    [pdpDelegationPolicyID_Params.protocolPolicyID_CS, pdpDelegationPolicyID_Params.delegationValidator_Hash],
                    ParamsDelegationPolicy
                ),
            };
            //--------------------------------------
            const pdpDelegationPolicyID_CS = mintingPolicyToId(pdpDelegationPolicyID_Script);
            console.log(`pdpDelegationPolicyID_CS ${pdpDelegationPolicyID_CS}`);
            //--------------------------------------
            const ParamsSchemaInvestUnitValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsInvestUnitValidator = Data.Static<typeof ParamsSchemaInvestUnitValidator>;
            const ParamsInvestUnitValidator = ParamsSchemaInvestUnitValidator as unknown as ParamsInvestUnitValidator;
            //--------------------------------------
            const pdpInvestUnitValidator_Params = {
                protocolPolicyID_CS: pdpProtocolPolicyID_CS,
                tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS,
            };
            //--------------------------------------
            const pdpInvestUnitValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsInvestUnitValidator>(
                    protocolFactory.fpInvestUnitValidator_Pre_CborHex.script,
                    [pdpInvestUnitValidator_Params.protocolPolicyID_CS, pdpInvestUnitValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsInvestUnitValidator
                ),
            };
            //--------------------------------------
            const pdpInvestUnitValidator_Hash = validatorToScriptHash(pdpInvestUnitValidator_Script);
            console.log(`pdpInvestUnitValidator_Hash ${pdpInvestUnitValidator_Hash}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_PREVIEW_NAME;
            const pdpInvestUnitValidator_AddressTestnet = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, pdpInvestUnitValidator_Script);
            console.log(`pdpInvestUnitValidator_AddressTestnet ${pdpInvestUnitValidator_AddressTestnet}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_MAINNET_NAME;
            const pdpInvestUnitValidator_AddressMainnet = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, pdpInvestUnitValidator_Script);
            console.log(`pdpInvestUnitValidator_AddressMainnet ${pdpInvestUnitValidator_AddressMainnet}`);
            //--------------------------------------
            // lucid.network = lucidNetwork;
            //--------------------------------------
            const fundFactory: FundFactory = {
                name: params.name,
                ffdpFundVersion: protocolFactory.fpFundVersion,
                ffdpFundPolicy_PreScript: protocolFactory.fpFundPolicy_Pre_CborHex,
                ffdpFundValidator_PreScript: protocolFactory.fpFundValidator_Pre_CborHex,
                ffdpFundHoldingPolicyID_PreScript: protocolFactory.fpFundHoldingPolicyID_Pre_CborHex,
                ffdpFundHoldingValidator_PreScript: protocolFactory.fpFundHoldingValidator_Pre_CborHex,
            };
            const protocol: ProtocolEntity = new ProtocolEntity({
                name: params.name,
                pdpProtocolVersion: protocolFactory.fpProtocolVersion,
                pdpSwapOfferVersion: protocolFactory.fpSwapOfferVersion,
                pdpBuyOrderVersion: protocolFactory.fpBuyOrderVersion,
                pdpDelegationVersion: protocolFactory.fpDelegationVersion,
                pdpInvestUnitVersion: protocolFactory.fpInvestUnitVersion,
                pdpScriptVersion: protocolFactory.fpScriptVersion,
                pdpProtocolPolicyID_CS,
                pdpProtocolPolicyID_Script,
                pdpProtocolPolicyID_Params,
                pdpProtocolValidator_AddressMainnet,
                pdpProtocolValidator_AddressTestnet,
                pdpProtocolValidator_Script,
                pdpProtocolValidator_Hash,
                pdpProtocolValidator_Params,
                pdpScriptPolicyID_CS,
                pdpScriptPolicyID_Script,
                pdpScriptPolicyID_Params,
                pdpScriptValidator_AddressMainnet,
                pdpScriptValidator_AddressTestnet,
                pdpScriptValidator_Script,
                pdpScriptValidator_Hash,
                pdpScriptValidator_Params,
                pdpSwapOfferPolicyID_CS,
                pdpSwapOfferPolicyID_Script,
                pdpSwapOfferPolicyID_Params,
                // pdpSwapOfferValidator_AddressMainnet: pdpSwapOfferValidator_AddressMainnetWithStake,
                // pdpSwapOfferValidator_AddressTestnet: pdpSwapOfferValidator_AddressTestnetWithStake,
                pdpSwapOfferValidator_AddressMainnet,
                pdpSwapOfferValidator_AddressTestnet,
                pdpSwapOfferValidator_Script,
                pdpSwapOfferValidator_Hash,
                pdpSwapOfferValidator_Params,
                pdpDelegationPolicyID_CS,
                pdpDelegationPolicyID_Script,
                pdpDelegationPolicyID_Params,
                pdpDelegationValidator_AddressMainnet,
                pdpDelegationValidator_AddressTestnet,
                pdpDelegationValidator_Script,
                pdpDelegationValidator_Hash,
                pdpDelegationValidator_Params,
                pdpInvestUnitValidator_AddressMainnet,
                pdpInvestUnitValidator_AddressTestnet,
                pdpInvestUnitValidator_Script,
                pdpInvestUnitValidator_Hash,
                pdpInvestUnitValidator_Params,
                pdpBuyOrderPolicyID_CS,
                pdpBuyOrderPolicyID_Script,
                pdpBuyOrderPolicyID_Params,
                pdpBuyOrderValidator_AddressMainnet,
                pdpBuyOrderValidator_AddressTestnet,
                pdpBuyOrderValidator_Script,
                pdpBuyOrderValidator_Hash,
                pdpBuyOrderValidator_Params,
                pdpFundFactories: [fundFactory],
            });
            //--------------------------------------
            const protocol_ = await ProtocolApi.createApi(protocol);
            //--------------------------------------
            await ProtocolApi.createHookApi(ProtocolEntity, protocol.getNet_Address(), protocol_.pdpProtocolPolicyID_CS);
            await ProtocolApi.createHookApi(ScriptEntity, protocol.getNet_Script_Validator_Address(), protocol_.pdpScriptPolicyID_CS);
            await ProtocolApi.createHookApi(SwapOfferEntity, protocol.getNet_SwapOffer_Validator_Address(), protocol_.pdpSwapOfferPolicyID_CS);
            await ProtocolApi.createHookApi(DelegationEntity, protocol.getNet_Delegation_Validator_Address(), protocol_.pdpDelegationPolicyID_CS);
            //--------------------------------------
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} created: ${protocol_.show()}`, false);
            return protocol_._DB_id;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnProtocolCreate - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error saving ${this._Entity.className()}: ${error}`);
            return undefined;
        }
    }

    public static async handleBtnProtocolUpdate(protocol: ProtocolEntity, params: ProtocolUpdateParams): Promise<boolean> {
        try {
            let updateParams = {
                name: params.name,
            };
            await ProtocolApi.updateMeWithParamsApi(protocol, updateParams);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} updated: ${protocol.show()}`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnProtocolUpdate - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error updating ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnProtocolAddFundFactory(protocol: ProtocolEntity, params: FundFactoryCreateParams): Promise<boolean> {
        try {
            //--------------------------------------
            if (params.configJson === undefined || params.configJson === '') {
                throw `Missing fields`;
            }
            //--------------------------------------
            let configJsonObject;
            let fundFactory: FundFactory;
            try {
                configJsonObject = JSON.parse(params.configJson);
                fundFactory = {
                    name: params.name,
                    ffdpFundVersion: configJsonObject.ffdpFundVersion,
                    ffdpFundPolicy_PreScript: getScriptFromJson(configJsonObject.ffdpFundPolicy_Pre_CborHex),
                    ffdpFundValidator_PreScript: getScriptFromJson(configJsonObject.ffdpFundValidator_Pre_CborHex),
                    ffdpFundHoldingPolicyID_PreScript: getScriptFromJson(configJsonObject.ffdpFundHoldingPolicyID_Pre_CborHex),
                    ffdpFundHoldingValidator_PreScript: getScriptFromJson(configJsonObject.ffdpFundHoldingValidator_Pre_CborHex),
                    ...configJsonObject,
                };
            } catch (error) {
                throw 'Invalid config json file ' + error;
            }
            //--------------------------------------
            if (
                protocol.pdpFundFactories.find((ff) => {
                    ff.name == fundFactory.name || ff.ffdpFundVersion == fundFactory.ffdpFundVersion;
                })
            ) {
                throw `Name or Version already exists`;
            }
            await ProtocolApi.addFundFactoryApi(protocol, fundFactory);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} updated: ${protocol.show()}`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnProtocolAddFundFactory - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error updating ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnProtocolDelete(instance: ProtocolEntity): Promise<boolean> {
        try {
            await ProtocolApi.deleteApi(instance);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} deleted: ${instance.show()}`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnProtocolDelete - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error deleting ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnCreateProtocolHooks(protocol: ProtocolEntity): Promise<boolean> {
        try {
            const addressesToFollowProtocol = await AddressToFollowApi.getByAddressApi(protocol.getNet_Address(), protocol.pdpProtocolPolicyID_CS);
            const addressesToFollowScript = await AddressToFollowApi.getByAddressApi(protocol.getNet_Script_Validator_Address(), protocol.pdpScriptPolicyID_CS);
            const addressesToFollowSwapOffer = await AddressToFollowApi.getByAddressApi(protocol.getNet_SwapOffer_Validator_Address(), protocol.pdpSwapOfferPolicyID_CS);
            const addressesToFollowDelegation = await AddressToFollowApi.getByAddressApi(protocol.getNet_Delegation_Validator_Address(), protocol.pdpDelegationPolicyID_CS);
            if (
                addressesToFollowProtocol &&
                addressesToFollowProtocol.length > 0 &&
                addressesToFollowScript &&
                addressesToFollowScript.length > 0 &&
                addressesToFollowSwapOffer &&
                addressesToFollowSwapOffer.length > 0 &&
                addressesToFollowDelegation &&
                addressesToFollowDelegation.length > 0
            ) {
                throw 'Webhooks already exists';
            } else {
                await ProtocolApi.createHookApi(ProtocolEntity, protocol.getNet_Address(), protocol.pdpProtocolPolicyID_CS);
                await ProtocolApi.createHookApi(ScriptEntity, protocol.getNet_Script_Validator_Address(), protocol.pdpScriptPolicyID_CS);
                await ProtocolApi.createHookApi(SwapOfferEntity, protocol.getNet_SwapOffer_Validator_Address(), protocol.pdpSwapOfferPolicyID_CS);
                await ProtocolApi.createHookApi(DelegationEntity, protocol.getNet_Delegation_Validator_Address(), protocol.pdpDelegationPolicyID_CS);
                pushSucessNotification(`${this._Entity.className()}`, `Webhooks created`, false);
                return true;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnCreateProtocolHooks - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error creating webhooks: ${error}`);
            return false;
        }
    }

    public static async handleBtnProtocolHooksSync(protocol: ProtocolEntity, force: boolean = false): Promise<boolean> {
        try {
            await ProtocolApi.syncWithAddressApi(ProtocolEntity, protocol.getNet_Address(), protocol.pdpProtocolPolicyID_CS, force);
            await ProtocolApi.syncWithAddressApi(ScriptEntity, protocol.getNet_Script_Validator_Address(), protocol.pdpScriptPolicyID_CS, force);
            await ProtocolApi.syncWithAddressApi(SwapOfferEntity, protocol.getNet_SwapOffer_Validator_Address(), protocol.pdpSwapOfferPolicyID_CS, force);
            await ProtocolApi.syncWithAddressApi(DelegationEntity, protocol.getNet_Delegation_Validator_Address(), protocol.pdpDelegationPolicyID_CS, force);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} syncronized`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnProtocolHooksSync - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error syncronizing ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnUpdateHistory(setProcessingMessage: (value: string) => void, forceStart?: boolean): Promise<boolean> {
        //--------------------------------------
        try {
            console.log(`${this._Entity.className()} - handleBtnUpdateHistory - Updating History...`);
            //--------------------------------------
            setProcessingMessage('Updating History...');
            //--------------------------------------
            const jobId = await ProtocolApi.updateHistoryApi(forceStart);
            //--------------------------------------
            console.log(`[${this._Entity.className()}] - handleBtnUpdateHistory - jobId: ${jobId}`);
            //--------------------------------------
            const { jobPromise } = JobManagerFrontEnd.startMonitoringJob(jobId, setProcessingMessage, 'Updating History');
            //--------------------------------------
            const job = await jobPromise; // Await the job's completion
            //--------------------------------------
            if (job.status === 'completed') {
                // await JobManagerFrontEnd.removeJob(jobId);
                const successMessage = job.message || `Updating History OK`;
                setProcessingMessage(successMessage);
                return true;
            } else if (job.status === 'failed') {
                // await JobManagerFrontEnd.removeJob(jobId);
                const errorMessage = job.error || `Updating History Error: ${job.message}`;
                setProcessingMessage(errorMessage);
                return false;
            } else if (job.status === 'canceled') {
                // await JobManagerFrontEnd.removeJob(jobId);
                const errorMessage = job.error || `Updating History Canceled`;
                setProcessingMessage(errorMessage);
                return false;
            } else {
                const errorMessage = job.error || `Updating History Unknown Status: ${job.status}`;
                setProcessingMessage(errorMessage);
                return false;
            }
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnUpdateHistory - Error: ${error_explained}`);
            setProcessingMessage(error_explained);
            return false;
        }
        //--------------------------------------
    }

    // #endregion front end Btn handlers

    // #region front end Btn tx handlers

    public static async handleBtnProtocolDeployTx(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: ProtocolDeployTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Deploying Protocol...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.deployTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()} - handleBtnProtocolDeployTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()} - handleBtnProtocolDeployTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Deploy Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()} - handleBtnProtocolDeployTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                // throw `Tx not confirmed`
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnProtocolDeployTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Deploy Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnProtocolUpdateTx(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: ProtocolUpdateTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Updating Protocol...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.updateTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()} - handleBtnProtocolUpdateTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()} - handleBtnProtocolUpdateTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Update Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()} - handleBtnProtocolUpdateTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                // throw `Tx not confirmed`
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnProtocolUpdateTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Update Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnProtocolAddScriptsTx(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        txParams: ProtocolAddScriptsTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Adding Protocol Scripts...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const protocolWithScripts: ProtocolEntity | undefined = await ProtocolApi.getByIdApi_(protocol._DB_id, {
                ...optionsGetMinimal,
                fieldsForSelect: ProtocolEntity.defaultFieldsAddScriptsTxScript,
            });
            if (protocolWithScripts === undefined) {
                throw `Invalid protocol id`;
            }
            //--------------------------------------
            const scriptsToAdd = [
                { hash: protocolWithScripts.pdpProtocolPolicyID_CS, script: protocolWithScripts.pdpProtocolPolicyID_Script },
                { hash: protocolWithScripts.pdpProtocolValidator_Hash, script: protocolWithScripts.pdpProtocolValidator_Script },
                { hash: protocolWithScripts.pdpScriptPolicyID_CS, script: protocolWithScripts.pdpScriptPolicyID_Script },
                { hash: protocolWithScripts.pdpScriptValidator_Hash, script: protocolWithScripts.pdpScriptValidator_Script },
                { hash: protocolWithScripts.pdpSwapOfferPolicyID_CS, script: protocolWithScripts.pdpSwapOfferPolicyID_Script },
                { hash: protocolWithScripts.pdpSwapOfferValidator_Hash, script: protocolWithScripts.pdpSwapOfferValidator_Script },
                // { hash: protocolWithScripts.pdpBuyOrderPolicyID_CS, script: protocolWithScripts.pdpBuyOrderPolicyID_Script },
                // { hash: protocolWithScripts.pdpBuyOrderValidator_Hash, script: protocolWithScripts.pdpBuyOrderValidator_Script },
                { hash: protocolWithScripts.pdpDelegationPolicyID_CS, script: protocolWithScripts.pdpDelegationPolicyID_Script },
                { hash: protocolWithScripts.pdpDelegationValidator_Hash, script: protocolWithScripts.pdpDelegationValidator_Script },
                { hash: protocolWithScripts.pdpInvestUnitValidator_Hash, script: protocolWithScripts.pdpInvestUnitValidator_Script },
            ];
            //--------------------------------------
            for (let index = 0; index < scriptsToAdd.length; index++) {
                //--------------------------------------
                const scriptToAdd = scriptsToAdd[index];
                //--------------------------------------
                const script_Hash: ScriptHash = scriptToAdd.hash;
                //--------------------------------------
                console.log(`${this._Entity.className()} - handleBtnProtocolAddScriptsTx - Adding Script: ${script_Hash}`);
                //--------------------------------------
                const script = await ScriptApi.getByHashApi(script_Hash);
                if (script !== undefined) {
                    console.log(`${this._Entity.className()} - handleBtnProtocolAddScriptsTx - Skipping script: ${script_Hash}, it's already added`);
                } else {
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Adding ${formatHash(script_Hash)}...`);
                    //--------------------------------------
                    const { txCborHex } = await ScriptTxApiCalls.addProtocolScriptTxApi(walletTxParams, { script_Hash, ...txParams });
                    //--------------------------------------
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Transaction prepared, waiting for sign to submit...`);
                    //--------------------------------------
                    const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
                    //--------------------------------------
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Transaction submitted, waiting for confirmation...`);
                    setProcessingTxHash(txHash);
                    //--------------------------------------
                    if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                        console.log(`${this._Entity.className()} - handleBtnProtocolAddScriptsTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                        //--------------------------------------
                        setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Syncronizing...`);
                        //--------------------------------------
                        pushSucessNotification(`${this._Entity.className()} Add Script Tx`, txHash, true);
                    } else {
                        console.log(`${this._Entity.className()} - handleBtnProtocolAddScriptsTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                        // throw `Tx not confirmed`
                    }
                    //--------------------------------------
                    if (!isEmulator) {
                        // como estoy haciendo mas de una tx y no quiero hacer todo el proceso de update wallet salvar emulador, si no hacerlo solo una vez al final,
                        // tengo que actualizar al menos las utxos de la wallet aqui de forma manual
                        // espero un poco para que se actualicen las utxos
                        await sleep(TX_PROPAGATION_DELAY_MS);
                    }
                    walletTxParams.utxos = await lucid.wallet().getUtxos();
                    //--------------------------------------
                }
            }
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnProtocolAddScriptsTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Add Scripts Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnProtocolDeleteTx(walletStore: IUseWalletStore, protocol: ProtocolEntity, txParams: ProtocolDeleteTxParams): Promise<boolean> {
        try {
            // const txPrepared: TxPrepared = await ProtocolActions.deleteTxApi(txParams);
            // // sign
            // // Submit
            // // waitForTxConfirmation
            // // sync
            // console.log(`${this._Entity.className()} - handleBtnDeleteTx - txPrepared: ${log(txPrepared)}`);
            // //--------------------------------------
            // pushSucessNotification(`${this._Entity.className()} Delete Tx`, txPrepared.txHash, true);
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnDeleteTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Delete Tx`, error_explained);
            // setProcessingTxMessage(error_explained);
            return false;
        }
    }

    // #endregion front end Btn tx handlers
}
